import { createSelector } from '@reduxjs/toolkit';
import { Euler, Matrix4 } from 'three';

import { TrussEnvelope } from '@paragon/contract-library';
import { Polygon2D, Polyhedron } from '@paragon/geometry-library';
import { calculateTrussExtents, calculateTrussSolidWithCache, getEnvelopeSegment, TrussExtents } from '@paragon/layout';
import { newVector3 } from '@paragon/three-shared';
import { min, notNullish, ReadonlyRecord } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import { convertFromLayoutToDesignCoordinates } from '../../library/layout-geometry/coordinates';
import { calculateBufferGeometryFromSolidData } from '../../library/layout-geometry/mesh-data-operations';
import { getTrussSolidCalculationDependencies } from '../../library/layout-geometry/truss-envelopes/trussSolidSelectors';
import { RootState } from '..';
import { getAllBearingEnvelopes } from './layoutDataSelectors';

export const getRoofGeometriesInLayout = createSelector(
  (state: RootState) => state.layout.roofContainers,
  (roofContainers) =>
    Object.values(roofContainers).map((container) => ({
      container,
      roofSolidBufferGeometry: container.solidData ? calculateBufferGeometryFromSolidData(container.solidData) : null,
      overhangBufferGeometries: (container.overhangSolidDataList ?? [])
        .filter(notNullish)
        .map(calculateBufferGeometryFromSolidData),
    })),
);

export const getRoofElevations = createSelector(getRoofGeometriesInLayout, (geometries) =>
  Object.fromEntries(
    geometries.map(({ container }) => [
      container.guid,
      container.solidData ? min(container.solidData.vertices.map((vertex) => vertex.z))! : null,
    ]),
  ),
);

export function calculateBufferGeometryForBearingEnvelopeShape(
  shape: Polygon2D,
  elevations: { top: number; bottom: number },
) {
  const baseVertices = shape.vertices.map(({ x, y }) => ({ x, y, z: elevations.bottom }));
  const topVertices = baseVertices.map(({ x, y }) => ({ x, y, z: elevations.top }));

  return calculateBufferGeometryFromSolidData({
    vertices: [...baseVertices, ...topVertices],
    faces: [
      { outer: [0, 1, 5, 4], inner: [] }, // Right
      { outer: [0, 3, 2, 1], inner: [] }, // Bottom
      { outer: [0, 4, 7, 3], inner: [] }, // Front
      { outer: [1, 2, 6, 5], inner: [] }, // Back
      { outer: [2, 3, 7, 6], inner: [] }, // Left
      { outer: [4, 5, 6, 7], inner: [] }, // Top
    ],
  });
}

export function useRoofElevation(roofContainerGuid: string | null) {
  const elevations = useAppSelector(getRoofElevations);

  if (roofContainerGuid == null) return 0;

  if (roofContainerGuid in elevations) {
    return elevations[roofContainerGuid];
  }

  throw new Error('Roof container elevation not found.');
}

/**
 * Attempts to infer a suitable elevation at which to render null-`levelGuid` truss envelopes, based on the elevation of
 * relevant bearing envelopes. Intended for use with roof plane-based truss envelopes, as they don't belong to a
 * container which can provide elevation information.
 */
export const getNullLevelDimensionElevation = createSelector(getAllBearingEnvelopes, (allEnvelopes) => {
  const lowestBearingTop = Math.min(...allEnvelopes.filter((e) => e.levelGuid == null).map((e) => e.top));
  return Number.isFinite(lowestBearingTop) ? lowestBearingTop : 0;
});

function calculateMatrix4ForTrussEnvelope(envelope: TrussEnvelope, trussExtents: TrussExtents): Matrix4 | null {
  const segment = getEnvelopeSegment(envelope);

  if (segment == null || envelope.componentDesignGuid == null) return null;

  const euler = new Euler(0, segment.direction.angleInRadians, 0);
  const position = convertFromLayoutToDesignCoordinates(trussExtents.backLeftPoint);

  return new Matrix4().makeRotationFromEuler(euler).setPosition(newVector3(position));
}

export const getAllTrussEnvelopeSolids = createSelector(
  getTrussSolidCalculationDependencies,
  (state: RootState) => state.layout.trussEnvelopes,

  (dependencies, trussEnvelopes): ReadonlyRecord<string, Polyhedron | null> =>
    Object.fromEntries(
      Object.values(trussEnvelopes).map((envelope) => [
        envelope.guid,
        calculateTrussSolidWithCache(envelope, dependencies).ok ?? null,
      ]),
    ),
);

export const getAllTrussEnvelopePositions = createSelector(
  (state: RootState) => state.layout.trussEnvelopes,

  (trussEnvelopes): ReadonlyRecord<string, Matrix4 | null> =>
    Object.fromEntries(
      Object.values(trussEnvelopes)
        .map((envelope) => {
          const trussExtents = calculateTrussExtents(envelope);

          return [envelope.guid, trussExtents && calculateMatrix4ForTrussEnvelope(envelope, trussExtents)];
        })
        .filter(notNullish),
    ),
);
